import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  type ApplyResult,
  addContributionToActive,
  openAsExploration,
} from '@/components/agent/tools/_shared/applyContribution'
import { getOrCreateInvestigationsStore } from '@/stores/investigations-store'
import type { GraphView } from '@/types/graph-view'

/**
 * How a result view lands on the case graph:
 * - `explore` opens it as a fresh transient exploration (leaving the active
 *   viewpoint), via {@link openAsExploration}.
 * - `add` additively merges it into the active viewpoint, via
 *   {@link addContributionToActive}.
 */
export type ApplyMode = 'explore' | 'add'

export type ApplyToGraph = (mode: ApplyMode, view: GraphView, label: string) => ApplyResult

/**
 * The "contribute this view, then go look at it on the graph" motion shared by
 * the agent tool-result cards, the hypothesis finding card, the OSINT graph
 * card, and the entity inspector.
 *
 * Resolves the per-case investigations store imperatively (via the module
 * cache, so it works with or without an `<InvestigationsStoreProvider>` in the
 * tree — same instance either way), applies the contribution by `mode`, then
 * navigates to the case graph route.
 *
 * Returns the {@link ApplyResult} so callers can branch on whether anything
 * actually merged (e.g. tailor a toast); navigation always fires. A missing
 * `caseId` is a no-op that returns an empty result.
 */
export function useApplyContributionToGraph(caseId: string | undefined): ApplyToGraph {
  const navigate = useNavigate()
  return useCallback(
    (mode, view, label) => {
      if (!caseId) return { investigationKey: null, contributionId: null }
      const store = getOrCreateInvestigationsStore({ caseId })
      const result =
        mode === 'explore'
          ? openAsExploration({ store, view, label })
          : addContributionToActive({ store, view, label })
      navigate(`/cases/${caseId}/graph`)
      return result
    },
    [caseId, navigate]
  )
}
